import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { EventCard } from './EventCard';
import {
  DEFAULT_EVENTS_VIEW_SETTINGS,
  getFilteredEvents,
  hasDefaultEventsViewSettings,
  normalizeEventsViewSettings,
  readEventsViewSettings,
  shouldFetchArchivedEvents,
  writeEventsViewSettings,
} from './eventsView';

const reducer = (state, payload) => ({ ...state, ...payload });

const inputCls =
  'mt-2 block w-full rounded-md border-0 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:focus:ring-white';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All statuses' },
  { value: 'archived', label: 'Archived' },
];

const dateOptions = [
  { value: 'all', label: 'Any date' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'current', label: 'Current' },
  { value: 'past', label: 'Past' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'unscheduled', label: 'Dates TBD' },
];

const publicOptions = [
  { value: 'all', label: 'Any' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const registrationOptions = [
  { value: 'all', label: 'Any' },
  { value: 'open', label: 'Open' },
  { value: 'frontEndDisabled', label: 'Front end disabled' },
  { value: 'external', label: 'External/no registration' },
];

const galleryOptions = [
  { value: 'all', label: 'Any' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const capacityOptions = [
  { value: 'all', label: 'Any' },
  { value: 'enabled', label: 'Limited' },
  { value: 'session', label: 'Session capacity' },
  { value: 'disabled', label: 'Not limited' },
];

const sortOptions = [
  { value: 'startDate|desc', label: 'Start date, newest first' },
  { value: 'startDate|asc', label: 'Start date, oldest first' },
  { value: 'endDate|desc', label: 'End date, newest first' },
  { value: 'updatedAt|desc', label: 'Recently updated' },
  { value: 'name|asc', label: 'Name A-Z' },
  { value: 'name|desc', label: 'Name Z-A' },
];

const SelectControl = ({ id, label, value, onChange, options }) => (
  <label htmlFor={id} className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
    {label}
    <select
      id={id}
      name={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const normalizeSettingsForUser = (settings, user) => {
  const normalized = normalizeEventsViewSettings(settings);
  if (user?.role > 2 && user?.crmManager !== true) {
    return { ...normalized, status: 'active' };
  }
  return normalized;
};

const Events = () => {
  const navigate = useNavigate();
  const { user } = useContext(firebaseAuth);
  const [viewSettings, setViewSettings] = useState(DEFAULT_EVENTS_VIEW_SETTINGS);
  const [initDone, setInitDone] = useState(false);
  const [data, setData] = useReducer(reducer, {
    items: [],
    isFetching: false,
    message: null,
  });

  const fetchAll = async (settings = viewSettings) => {
    const normalizedSettings = normalizeSettingsForUser(settings, user);
    setData({ isFetching: true, message: null });
    try {
      await firebase.auth();
      const callable = firebase.functions().httpsCallable('appV2/getEventData');
      const result = await callable({
        showArchive: shouldFetchArchivedEvents(normalizedSettings),
      });
      setInitDone(true);
      setData({ items: result.data || [], isFetching: false });
    } catch (error) {
      console.log(error);
      setData({ isFetching: false, message: 'Could not load events.' });
    }
  };

  useEffect(() => {
    if (!user?.isAuthenticated) {
      navigate('/manage/sign-in');
      return;
    }
    const savedSettings = normalizeSettingsForUser(readEventsViewSettings(user), user);
    setViewSettings(savedSettings);
    void fetchAll(savedSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.isAuthenticated && initDone) void fetchAll(viewSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewSettings.status]);

  const updateViewSetting = (key, value) => {
    setViewSettings((current) => {
      const next = normalizeSettingsForUser({ ...current, [key]: value }, user);
      writeEventsViewSettings(user, next);
      return next;
    });
  };

  const resetViewSettings = () => {
    const next = normalizeSettingsForUser(DEFAULT_EVENTS_VIEW_SETTINGS, user);
    setViewSettings(next);
    writeEventsViewSettings(user, next);
  };

  const filteredEvents = useMemo(
    () => getFilteredEvents(data.items, viewSettings),
    [data.items, viewSettings],
  );
  const isDefaultView = hasDefaultEventsViewSettings(viewSettings);
  const canViewArchived = user?.role <= 2 || user?.crmManager === true;

  const EventsGrid = ({ items }) => (
    <section className="mt-6">
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((g, i) => (
          <EventCard
            key={g.id}
            item={g}
            id={i}
            index={i}
            updateData={() => fetchAll(viewSettings)}
          />
        ))}
        {items.length === 0 && (
          <div className="rounded-md bg-zinc-50 px-4 py-6 text-sm text-zinc-500 ring-1 ring-zinc-200 sm:col-span-2 lg:col-span-3 xl:col-span-4 dark:bg-zinc-900 dark:text-zinc-400 dark:ring-zinc-700">
            No events match the current filters.
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div id="events">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Events</h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {filteredEvents.length} of {data.items.length} events
          </p>
        </div>
        {user?.role <= 2 && (
          <div className="flex items-center gap-2">
            <NavLink
              to="/manage/event"
              className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              New event
            </NavLink>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label htmlFor="searchText" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Search
            <input
              id="searchText"
              type="text"
              name="searchText"
              value={viewSettings.searchText}
              onChange={(e) => updateViewSetting('searchText', e.target.value)}
              className={inputCls}
              placeholder="Name, URL, code, or ID..."
            />
          </label>

          {canViewArchived && (
            <SelectControl
              id="eventStatus"
              label="Status"
              value={viewSettings.status}
              onChange={(value) => updateViewSetting('status', value)}
              options={statusOptions}
            />
          )}

          <SelectControl
            id="eventDateWindow"
            label="Date"
            value={viewSettings.dateWindow}
            onChange={(value) => updateViewSetting('dateWindow', value)}
            options={dateOptions}
          />

          <SelectControl
            id="eventRegistrationState"
            label="Registration"
            value={viewSettings.registrationState}
            onChange={(value) => updateViewSetting('registrationState', value)}
            options={registrationOptions}
          />

          <SelectControl
            id="eventPublicState"
            label="Landing page"
            value={viewSettings.publicState}
            onChange={(value) => updateViewSetting('publicState', value)}
            options={publicOptions}
          />

          <SelectControl
            id="eventGalleryState"
            label="Gallery"
            value={viewSettings.galleryState}
            onChange={(value) => updateViewSetting('galleryState', value)}
            options={galleryOptions}
          />

          <SelectControl
            id="eventCapacityState"
            label="Capacity"
            value={viewSettings.capacityState}
            onChange={(value) => updateViewSetting('capacityState', value)}
            options={capacityOptions}
          />

          <SelectControl
            id="eventSortOrder"
            label="Sort"
            value={viewSettings.sortOrder}
            onChange={(value) => updateViewSetting('sortOrder', value)}
            options={sortOptions}
          />
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <FunnelIcon className="size-4" aria-hidden="true" />
            <span>
              {filteredEvents.length} result{filteredEvents.length === 1 ? '' : 's'}
            </span>
          </div>
          {!isDefaultView && (
            <button
              type="button"
              onClick={resetViewSettings}
              className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-zinc-700 ring-1 ring-inset ring-zinc-300 hover:bg-white dark:text-zinc-200 dark:ring-zinc-600 dark:hover:bg-zinc-800"
            >
              <ArrowPathIcon className="size-4" aria-hidden="true" />
              Reset filters
            </button>
          )}
        </div>
      </div>

      {data.isFetching && (
        <div className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">Loading events…</div>
      )}
      {data.message && (
        <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
          {data.message}
        </div>
      )}

      <EventsGrid items={filteredEvents} />
    </div>
  );
};

export default Events;
